import React, { ChangeEvent, useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
  Typography
} from '@mui/material';
import CardMedia from '@mui/material/CardMedia';
import { LinkItem, useUpload } from '../hooks/useUpload';
import { Swiper, SwiperSlide } from 'swiper/react';
import { AsyncImage } from './AsyncImage/AsyncImage';
import CircularProgress from '@mui/material/CircularProgress';
import { Navigation, Pagination, Scrollbar, A11y } from 'swiper/modules';
// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';

const allowedTypes = [
  'image/jpeg',
  'image/png',
  'application/pdf',
  'video/mp4',
  'video/quicktime',
  'audio/mpeg',
  'audio/wav'
  // Add more supported types as needed
];

export type ImageUploadProps = {
  onS3UploadCallback: (payload: LinkItem[]) => void;
  links: LinkItem[];
};

export const PrizetS3Upload = (props: ImageUploadProps) => {
  const { uploadFile, setFiles, files, uploading, bucketLinks } = useUpload();
  const [linkImages, setLinks] = useState<LinkItem[]>([]);
  const { links } = props;
  const [loading, setLoading] = useState(false);

  const handleFileChange = (event) => {
    const selectedFiles = event.target.files;
    const filesArray: File[] = Array.from(selectedFiles);
    const filteredFiles = filesArray.filter((file: File) =>
      allowedTypes.includes(file.type)
    );

    setFiles(filteredFiles);
    setLoading(true);
  };

  useEffect(() => {
    if (bucketLinks.length === 0) return;
    setLoading(false);
    const dedupe = [...new Set([...links, ...bucketLinks])];
    setLinks(dedupe);
    props.onS3UploadCallback(dedupe);
  }, [bucketLinks]);

  useEffect(() => {
    setLinks(links);
    props.onS3UploadCallback(links);
  }, []);

  // Fire this when the remove is triggered

  useEffect(() => {
    props.onS3UploadCallback(linkImages);
  }, [linkImages]);

  const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1
  });

  return (
    <Box display={'flex'} flexDirection={'column'} columnGap={2}>
      <Box display={'flex'} flexDirection={'row'}>
        <Button
          style={{ color: 'white' }}
          component="label"
          role={'button'}
          variant="contained"
          tabIndex={-1}
          startIcon={<CloudUploadIcon />}
        >
          Upload files
          <VisuallyHiddenInput
            type="file"
            onChange={handleFileChange}
            multiple
          />
        </Button>
      </Box>

      {loading && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{ marginTop: 2 }}
        >
          <CircularProgress />
          <Typography sx={{ marginLeft: 2 }}>Uploading...</Typography>
        </Box>
      )}

      {linkImages.length > 0 && !loading && (
        <Box sx={{ width: 345, padding: '2%' }}>
          <Card sx={{ maxWidth: 'auto' }}>
            <Swiper
              navigation
              // pagination={{ clickable: true }}
              // scrollbar={{ draggable: true }}
              modules={[Navigation, Pagination, Scrollbar, A11y]}
              spaceBetween={50}
              slidesPerView={1}
            >
              {linkImages.map((item, index) => (
                <SwiperSlide key={index}>
                  <div style={{ marginLeft: '15%' }} key={item.image}>
                    <AsyncImage
                      h={250}
                      w={250}
                      url={item.image}
                      key={item.image}
                    />
                    <Box
                      sx={{
                        padding: '2%',
                        justifyContent: 'center',
                        fontWeight: 'bold'
                      }}
                      display={'flex'}
                    >
                      {index + 1}/{linkImages.length}
                    </Box>
                    <Checkbox
                      checked={item.isThumbnail}
                      onChange={(e) => {
                        item = { ...item, isThumbnail: !item.isThumbnail };
                        const temporaryItem = linkImages[index];

                        linkImages[index] = item;

                        setLinks([
                          ...linkImages.map((record) => ({
                            ...record,
                            isThumbnail:
                              record.image === item.image
                                ? item.isThumbnail
                                : false
                          }))
                        ]);
                        e.currentTarget.checked = item.isThumbnail;
                      }}
                    />{' '}
                    <span> Set as thumbnail </span>
                    <Button
                      onClick={() => {
                        linkImages.splice(index, 1);
                        setLinks([...linkImages]);
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default function ImgMediaCard({ image }: { image: string }) {
  return (
    <Card sx={{ maxWidth: '100%' }}>
      <CardMedia component="img" height="140" image={''} />
    </Card>
  );
}
